'use client';

import { c as _c } from "react/compiler-runtime";
import React, { MouseEvent } from 'react';
import { Dictionary } from '@/src/lib/dictionary/types.ts';
import { ExtendedProductListItem } from '@/src/lib/catalog-search/types.ts';
import { usePathname } from 'next/navigation';
import classNames from 'classnames';
import Loading from '@/src/app/components/LoadingSpinner/LoadingSpinner.tsx';
import { calculateFullScalePrice, getFormattedPriceWithLocaleCurrency } from '@/src/lib/product/business/product-prices.ts';
import { eventBus } from '@/src/lib/core/event/bus.ts';
import { EventBusName } from '@/src/lib/core/event/types.ts';
import { getConcreteProductSkuFromItem } from '@/src/lib/product/business/product-item.ts';
import { YourPriceResponseWithCurrency } from '@/src/lib/your-price/types.ts';
import PriceInfo from '@/src/app/[country]/[lang]/shop/[...segments]/(categories)/components/CalculatePriceButton/PriceInfo.client.tsx';
import PriceInfoModalContent from '@/src/app/[country]/[lang]/shop/[...segments]/(categories)/components/CalculatePriceButton/PriceInfoModalContent.tsx';
import { ServerActionResult } from '@/src/lib/http/types.ts';
import { GetUser } from '@/src/lib/auth/types.ts';
import { isUserGuest, isUserRegistered, isUserUnregistered, isUserVerified } from '@/src/lib/auth/business/user-status.ts';
import { getDialectForLocale } from '@/src/lib/dictionary/utils.ts';
type CalculatePriceButtonProps = {
  dict: Dictionary;
  product: ExtendedProductListItem;
  user: GetUser;
  lang: string;
  country: string;
  className?: string;
  accountNumber?: string;
  pricingReference?: string;
  isLoading: boolean;
  isRefetching?: boolean;
  isError: boolean;
  error: Error | null;
  onCalculatePriceClick: (event?: MouseEvent, queryStockQuantity?: boolean) => Promise<void | ServerActionResult<null, string>>;
  yourPriceData: YourPriceResponseWithCurrency | null;
  currencyIsoCode: string;
};
export function GuestCalculatePriceButton(t0) {
  const $ = _c(6);
  const {
    dict,
    lang,
    className
  } = t0;
  const pathname = usePathname();
  let t1;
  if ($[0] !== lang) {
    t1 = getDialectForLocale(lang);
    $[0] = lang;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `/auth/login?ui_locales=${t1}${pathname ? `&returnTo=${pathname}` : ""}`;
  let t3;
  if ($[2] !== className || $[3] !== dict.product.calculateNow || $[4] !== t2) {
    t3 = <a href={t2} className={className}>{dict.product.calculateNow}</a>;
    $[2] = className;
    $[3] = dict.product.calculateNow;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
export function UnregisteredCalculatePriceButton(t0) {
  const $ = _c(4);
  const {
    dict,
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = function onButtonClick() {
      eventBus.emit(EventBusName.CHECKOUT_CLICK_OPEN_REGISTER_MODAL);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const onButtonClick = t1;
  let t2;
  if ($[1] !== className || $[2] !== dict.product.calculateNow) {
    t2 = <button className={className} onClick={onButtonClick}>{dict.product.calculateNow}</button>;
    $[1] = className;
    $[2] = dict.product.calculateNow;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function RegisteredCalculatePriceButton(t0) {
  const $ = _c(3);
  const {
    dict,
    className
  } = t0;
  let t1;
  if ($[0] !== className || $[1] !== dict.product.calculateNow) {
    t1 = <button className={className} disabled={true}>{dict.product.calculateNow}</button>;
    $[0] = className;
    $[1] = dict.product.calculateNow;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
export function VerifiedCalculatePriceButton(t0) {
  const $ = _c(14);
  const {
    dict,
    className,
    lang,
    isLoading,
    isRefetching,
    onCalculatePriceClick,
    yourPriceData
  } = t0;
  if (isLoading || isRefetching) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="h-6"><Loading small={true} /></div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const yourPriceNetPrice = yourPriceData && yourPriceData.items[0].netPrice;
  if (yourPriceNetPrice) {
    let t1;
    if ($[1] !== yourPriceNetPrice) {
      t1 = calculateFullScalePrice(yourPriceNetPrice).toFixed(2);
      $[1] = yourPriceNetPrice;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    const t2 = yourPriceNetPrice || 0;
    let t3;
    if ($[3] !== lang || $[4] !== t2 || $[5] !== yourPriceData.currencyIsoCode) {
      t3 = getFormattedPriceWithLocaleCurrency(t2, yourPriceData.currencyIsoCode, lang);
      $[3] = lang;
      $[4] = t2;
      $[5] = yourPriceData.currencyIsoCode;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    let t4;
    if ($[7] !== t1 || $[8] !== t3) {
      t4 = <span itemProp="price" className="font-semibold" data-testid="total-price" content={t1}>{t3}</span>;
      $[7] = t1;
      $[8] = t3;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    return t4;
  }
  let t1;
  if ($[10] !== className || $[11] !== dict.product.calculateNow || $[12] !== onCalculatePriceClick) {
    t1 = <button className={className} onClick={onCalculatePriceClick}>{dict.product.calculateNow}</button>;
    $[10] = className;
    $[11] = dict.product.calculateNow;
    $[12] = onCalculatePriceClick;
    $[13] = t1;
  } else {
    t1 = $[13];
  }
  return t1;
}
function isAbleToDisplayPriceInfo(user: GetUser, accountNumber?: string) {
  return isUserVerified(user) && accountNumber !== undefined;
}
function CalculatePriceButton(t0) {
  const $ = _c(51);
  const {
    dict,
    user,
    lang,
    country,
    className,
    product,
    pricingReference,
    accountNumber,
    error,
    isError,
    isLoading,
    onCalculatePriceClick,
    yourPriceData,
    currencyIsoCode
  } = t0;
  let t1;
  if ($[0] !== product) {
    t1 = getConcreteProductSkuFromItem(product);
    $[0] = product;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const sku = t1;
  let t2;
  if ($[2] !== accountNumber || $[3] !== user) {
    t2 = isAbleToDisplayPriceInfo(user, accountNumber);
    $[2] = accountNumber;
    $[3] = user;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const displayPriceInfo = t2;
  if (!sku) {
    return null;
  }
  let t3;
  if ($[5] !== onCalculatePriceClick) {
    t3 = async function onModalOpen() {
      await onCalculatePriceClick(undefined, false);
    };
    $[5] = onCalculatePriceClick;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const onModalOpen = t3;
  let t4;
  if ($[7] !== className) {
    t4 = classNames(className, "text-sm");
    $[7] = className;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== country || $[10] !== currencyIsoCode || $[11] !== dict || $[12] !== displayPriceInfo || $[13] !== error || $[14] !== isLoading || $[15] !== lang || $[16] !== onModalOpen || $[17] !== product || $[18] !== yourPriceData) {
    t5 = displayPriceInfo ? <PriceInfo dict={dict} queryYourPrice={yourPriceData === null} onModalOpen={onModalOpen} lang={lang} country={country} priceInfoModalContent={<PriceInfoModalContent dict={dict} isLoading={isLoading} yourPriceData={yourPriceData} lang={lang} error={error} currencyIsoCode={currencyIsoCode} priceGroup={product.extendedAbstractProduct?.["concrete-products"][0].priceGroup} productListPrice={product.extendedAbstractProduct?.["concrete-products"][0]["concrete-product-prices"]?.[0].price} />} /> : null;
    $[9] = country;
    $[10] = currencyIsoCode;
    $[11] = dict;
    $[12] = displayPriceInfo;
    $[13] = error;
    $[14] = isLoading;
    $[15] = lang;
    $[16] = onModalOpen;
    $[17] = product;
    $[18] = yourPriceData;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== dict.product.yourPrice) {
    t6 = <span className="text-gray-400 pr-2">{dict.product.yourPrice}:</span>;
    $[20] = dict.product.yourPrice;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== dict || $[23] !== lang || $[24] !== user) {
    t7 = isUserGuest(user) ? <GuestCalculatePriceButton className="font-semibold underline hover:no-underline" dict={dict} lang={lang} /> : null;
    $[22] = dict;
    $[23] = lang;
    $[24] = user;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== dict || $[27] !== user) {
    t8 = isUserUnregistered(user) ? <UnregisteredCalculatePriceButton className="font-semibold underline hover:no-underline" dict={dict} /> : null;
    $[26] = dict;
    $[27] = user;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== dict || $[30] !== user) {
    t9 = isUserRegistered(user) ? <RegisteredCalculatePriceButton className="font-semibold underline hover:no-underline" dict={dict} /> : null;
    $[29] = dict;
    $[30] = user;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== accountNumber || $[33] !== dict || $[34] !== isError || $[35] !== isLoading || $[36] !== lang || $[37] !== onCalculatePriceClick || $[38] !== pricingReference || $[39] !== sku || $[40] !== user || $[41] !== yourPriceData) {
    t10 = isUserVerified(user) ? <VerifiedCalculatePriceButton className="font-semibold underline hover:no-underline" dict={dict} lang={lang} sku={sku} pricingReference={pricingReference} accountNumber={accountNumber} isLoading={isLoading} isError={isError} yourPriceData={yourPriceData} onCalculatePriceClick={onCalculatePriceClick} /> : null;
    $[32] = accountNumber;
    $[33] = dict;
    $[34] = isError;
    $[35] = isLoading;
    $[36] = lang;
    $[37] = onCalculatePriceClick;
    $[38] = pricingReference;
    $[39] = sku;
    $[40] = user;
    $[41] = yourPriceData;
    $[42] = t10;
  } else {
    t10 = $[42];
  }
  let t11;
  if ($[43] !== t10 || $[44] !== t4 || $[45] !== t5 || $[46] !== t6 || $[47] !== t7 || $[48] !== t8 || $[49] !== t9) {
    t11 = <div className={t4} data-testid="calculate-your-price">{t5}{t6}{t7}{t8}{t9}{t10}</div>;
    $[43] = t10;
    $[44] = t4;
    $[45] = t5;
    $[46] = t6;
    $[47] = t7;
    $[48] = t8;
    $[49] = t9;
    $[50] = t11;
  } else {
    t11 = $[50];
  }
  return t11;
}
export default CalculatePriceButton;